'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  useLocale,
  useBlockPart,
  BlockIcon,
  useMarketplaceSource,
  type MarketplaceOrder,
} from '@akademi/site-builder-runtime';
import { Button, Card, CardContent, CardHeader, CardTitle, Separator } from '@akademi/ui';
import { Loader2, Trash2 } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { tx } from '../_shared/coerce';
import { textTertiary } from '../_shared/styles';
import { formatMarketPrice, mpSurface, type MarketPrice } from './_shared';
import { useMarketplaceCart } from './_useMarketplaceCart';
import { CourseCoverImage } from '../_shared/CourseCoverImage';

type Localized = string | Record<string, string>;

export interface MarketplaceCartLineItem {
  id?: string;
  image?: string;
  title?: Localized;
  href?: string;
  price?: MarketPrice;
  listPrice?: MarketPrice;
  free?: boolean;
}

export interface MarketplaceCartItemsProps {
  title?: Localized;
  icon?: string;
  /** `'cart'` (default): the live `/marketplace/cart` — removable rows.
   *  `'order'`: one placed order's line items instead — read-only, no
   *  remove button. Pairs with {@link MarketplaceCheckoutCta}'s own
   *  `mode="order"` for `/marketplace/orders/[id]`. */
  mode?: 'cart' | 'order';
  /** Required when `mode="order"` — bound from the route's `[id]` segment
   *  by `bindPathParams.ts`. Internal only: not exposed in the block's
   *  schema, so operators can't set it from the admin UI. */
  orderId?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_ITEMS: MarketplaceCartLineItem[] = [
  {
    id: 'coaching-101',
    title: 'Coaching Conversations 101',
    price: { amount: '349.00', currency: 'TRY' },
    listPrice: { amount: '499.00', currency: 'TRY' },
  },
  {
    id: 'leadership-bundle',
    title: 'Leadership Essentials (bundle)',
    price: { amount: '1499.00', currency: 'TRY' },
  },
];

interface Row {
  key: string;
  image?: string;
  title: string;
  href?: string;
  price?: MarketPrice;
  listPrice?: MarketPrice;
  free?: boolean;
}

/**
 * Cart-items list — akademi-lms-v2's `ShoppingCartPage` "Items in cart"
 * card. With a {@link useMarketplaceCart} source mounted it shows the live
 * `/marketplace/cart` rows and "remove" hits the real endpoint; with no
 * source it renders a demo default and removal is local-only — the
 * "fallbacks stay, for testing" contract.
 */
export function MarketplaceCartItems({
  title,
  icon = 'shopping-cart',
  mode = 'cart',
  orderId,
  paddingOuter,
  paddingInner,
}: MarketplaceCartItemsProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const part = useBlockPart();
  const isOrderMode = mode === 'order';
  const source = useMarketplaceSource();
  const cart = useMarketplaceCart();

  const [localRows, setLocalRows] = useState<MarketplaceCartLineItem[]>(DEFAULT_ITEMS);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [orderItems, setOrderItems] = useState<MarketplaceOrder['items']>();
  const [orderLoadStatus, setOrderLoadStatus] = useState<'loading' | 'ready' | 'error'>(
    isOrderMode && source && orderId ? 'loading' : 'ready',
  );

  useEffect(() => {
    if (!isOrderMode || !source || !orderId) return;
    let cancelled = false;
    setOrderLoadStatus('loading');
    source
      .getOrder(orderId)
      .then((order) => {
        if (cancelled) return;
        setOrderItems(order?.items ?? []);
        setOrderLoadStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setOrderLoadStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isOrderMode, source, orderId]);

  const rows: Row[] = isOrderMode
    ? (orderItems ?? []).map((it, i) => ({
        key: String(i),
        image: it.thumbnailUrl,
        title: it.title,
        href: it.href,
        price: it.price,
        listPrice: it.listPrice,
        free: it.free,
      }))
    : cart
      ? (cart.cart?.items ?? []).map((it) => ({
          key: it.cartItemId,
          image: it.thumbnailUrl,
          title: it.title,
          href: it.publicPath,
          price: it.price,
          listPrice: it.listPrice,
          free: it.freeItem,
        }))
      : localRows.map((it, i) => ({
          key: it.id ?? String(i),
          image: it.image,
          title: tx(it.title),
          href: it.href,
          price: it.price,
          listPrice: it.listPrice,
          free: it.free,
        }));

  const removeRow = (key: string) => {
    if (cart) void cart.remove(key).catch(() => {});
    else setLocalRows((prev) => prev.filter((r, i) => (r.id ?? String(i)) !== key));
    setConfirmId(null);
  };

  const isLoading = isOrderMode && orderLoadStatus === 'loading';
  const heading = isOrderMode
    ? `${tx(title) || t('marketplace.orderDetail.title', { defaultValue: 'Order' })}${orderId ? ` #${orderId}` : ''}`
    : tx(title) || t('marketplace.cartItems.title');

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardHeader className="p-0">
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
            <BlockIcon value={icon} size={18} />
            {isOrderMode ? heading : `${heading} (${rows.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div
              style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8, 32px)' }}
            >
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : isOrderMode && orderLoadStatus === 'error' ? (
            <p style={{ color: 'var(--color-text-error, #b91c1c)' }}>
              {t('marketplace.orderDetail.error', {
                defaultValue: "Couldn't load this order. Please try again.",
              })}
            </p>
          ) : rows.length === 0 ? (
            <div
              style={{ ...textTertiary, textAlign: 'center', padding: 'var(--space-6, 24px) 0' }}
            >
              {isOrderMode
                ? t('marketplace.orderDetail.empty', {
                    defaultValue: 'This order has no line items.',
                  })
                : t('marketplace.cartItems.empty')}
            </div>
          ) : (
            <div {...part('list')} style={{ display: 'flex', flexDirection: 'column' }}>
              {rows.map((item, i) => {
                const key = item.key;
                const priceText = formatMarketPrice(item.price, locale);
                const listPriceText = formatMarketPrice(item.listPrice, locale);
                return (
                  <div key={key}>
                    {i > 0 && <Separator />}
                    <div
                      {...part('item', i)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3, 12px)',
                        padding: 'var(--space-3, 12px) 0',
                      }}
                    >
                      <div
                        aria-hidden
                        style={{
                          position: 'relative',
                          width: 72,
                          height: 48,
                          flexShrink: 0,
                          overflow: 'hidden',
                          borderRadius: 'var(--radius-sm, 4px)',
                        }}
                      >
                        <CourseCoverImage
                          image={item.image}
                          resizeTo="144x0"
                          sizes="72px"
                          padding="10%"
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {item.href ? (
                          <PortalLink
                            href={item.href}
                            style={{
                              color: 'var(--color-text-primary, #18181b)',
                              fontWeight: 600,
                              textDecoration: 'none',
                            }}
                          >
                            {item.title}
                          </PortalLink>
                        ) : (
                          <span style={{ fontWeight: 600 }}>{item.title}</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {item.free ? (
                          <strong style={{ color: 'var(--color-success-600, #059669)' }}>
                            {t('marketplace.cartItems.free')}
                          </strong>
                        ) : (
                          <>
                            <div style={{ fontWeight: 700 }}>{priceText}</div>
                            {listPriceText && listPriceText !== priceText && (
                              <div
                                style={{
                                  ...textTertiary,
                                  fontSize: 'var(--font-size-xs, 12px)',
                                  textDecoration: 'line-through',
                                }}
                              >
                                {listPriceText}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {isOrderMode ? null : confirmId === key ? (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => removeRow(key)}
                          >
                            {t('marketplace.cartItems.remove')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmId(null)}
                          >
                            {t('marketplace.cartItems.cancel')}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          aria-label={t('marketplace.cartItems.remove')}
                          onClick={() => setConfirmId(key)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </SectionShell>
  );
}
